import { useMemo, useState } from "react";
import SlideFrame from "./_SlideFrame";
import { fmt, fmtInt, PRICING } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { TrendingDown } from "lucide-react";

/**
 * IBA Servicing annual cost model:
 *   active_loans = current + past_due
 *   monthly_servicing = current * $3 + past_due * $10
 *   effective_monthly = max(monthly_servicing, $500 minimum)
 *   setup_cost = new_originations_annual * $5
 *   onboarding_one_time = $5,000 (year 1 only)
 *   year_1 = effective_monthly*12 + setup_cost + $5,000
 *   year_N (N>=2) = effective_monthly*12 + setup_cost
 *
 * Competitor annual cost = active_loans * avg_upb * competitor_pct
 */

export default function SlideSavingsCalculator() {
  const [activeLoans, setActiveLoans] = useState(5000);
  const [avgUpb, setAvgUpb] = useState(8500);
  const [competitorPct, setCompetitorPct] = useState(1.0);
  const [delinquencyPct, setDelinquencyPct] = useState(8);
  const [newOrigPerMonth, setNewOrigPerMonth] = useState(400);
  const [ancillaryPerLoanYear, setAncillaryPerLoanYear] = useState(45);

  const calc = useMemo(() => {
    const pastDue = Math.round(activeLoans * (delinquencyPct / 100));
    const current = activeLoans - pastDue;
    const monthlyServicing = current * PRICING.current_per_month + pastDue * PRICING.past_due_per_month;
    const effectiveMonthly = Math.max(monthlyServicing, PRICING.monthly_minimum);
    const setupAnnual = newOrigPerMonth * 12 * PRICING.setup_per_account;

    const instoreY1 = effectiveMonthly * 12 + setupAnnual + PRICING.onboarding_one_time;
    const instoreYN = effectiveMonthly * 12 + setupAnnual;

    const competitorAnnual = activeLoans * avgUpb * (competitorPct / 100);

    // IBA implied bps (for benchmark display)
    const totalUpb = activeLoans * avgUpb;
    const ibaBps = totalUpb > 0 ? ((effectiveMonthly * 12) / totalUpb) * 10000 : 0;

    // 50/50 revenue share returned to partner
    const ancillaryAnnual = activeLoans * ancillaryPerLoanYear;
    const revenueShareBack = ancillaryAnnual * 0.5;

    const y1Savings = competitorAnnual - instoreY1;
    const yNSavings = competitorAnnual - instoreYN;
    const y1EconomicGain = y1Savings + revenueShareBack;

    const threeYearInstore = instoreY1 + instoreYN + instoreYN;
    const threeYearCompetitor = competitorAnnual * 3;
    const threeYearSavings = threeYearCompetitor - threeYearInstore;
    const threeYearRevShare = revenueShareBack * 3;
    const threeYearGain = threeYearSavings + threeYearRevShare;

    const chartData = [
      { year: "Year 1", "IBA Servicing": instoreY1, Traditional: competitorAnnual },
      { year: "Year 2", "IBA Servicing": instoreY1 + instoreYN, Traditional: competitorAnnual * 2 },
      { year: "Year 3", "IBA Servicing": threeYearInstore, Traditional: threeYearCompetitor },
    ];

    return {
      current, pastDue, monthlyServicing, effectiveMonthly,
      instoreY1, instoreYN, competitorAnnual,
      y1Savings, yNSavings,
      threeYearInstore, threeYearCompetitor, threeYearSavings,
      ancillaryAnnual, revenueShareBack,
      y1EconomicGain, threeYearRevShare, threeYearGain,
      ibaBps,
      chartData,
      savingsPct: competitorAnnual > 0 ? (y1Savings / competitorAnnual) : 0,
    };
  }, [activeLoans, avgUpb, competitorPct, delinquencyPct, newOrigPerMonth, ancillaryPerLoanYear]);

  return (
    <SlideFrame tag="04 · Economics">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <p className="overline mb-4">Interactive</p>
          <h2 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95] font-medium max-w-4xl">
            Your savings, <em className="italic text-primary">modeled live</em>.
          </h2>
        </div>
        <p className="text-sm text-muted-foreground max-w-sm">
          Adjust your portfolio inputs. We compare IBA Servicing&rsquo;s flat fees against a traditional
          percentage-of-UPB servicer.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Inputs */}
        <div className="lg:col-span-5 border border-foreground/20 bg-white p-8">
          <p className="overline mb-6">Portfolio inputs</p>
          <div className="space-y-7">
            <NumberField
              id="active-loans" label="Active loans" suffix="accounts"
              value={activeLoans} min={100} step={100}
              onChange={setActiveLoans}
            />
            <NumberField
              id="avg-upb" label="Average UPB per loan" prefix="$"
              value={avgUpb} min={100} step={100}
              onChange={setAvgUpb}
            />
            <NumberField
              id="competitor-pct" label="Traditional servicer rate" suffix="% of UPB / year"
              value={competitorPct} min={0} step={0.05} isFloat
              onChange={setCompetitorPct}
            />
            <NumberField
              id="delinquency-pct" label="Delinquency rate" suffix="% of portfolio"
              value={delinquencyPct} min={0} max={100} step={0.5} isFloat
              onChange={setDelinquencyPct}
            />
            <NumberField
              id="new-orig" label="New originations per month" suffix="accounts / mo"
              value={newOrigPerMonth} min={0} step={10}
              onChange={setNewOrigPerMonth}
            />
            <NumberField
              id="ancillary-per-loan" label="Ancillary revenue / loan / year" prefix="$"
              value={ancillaryPerLoanYear} min={0} step={1}
              onChange={setAncillaryPerLoanYear}
            />
          </div>

          <div className="mt-8 pt-6 border-t border-foreground/20 text-xs font-mono-data text-muted-foreground leading-relaxed">
            <p>Current: <span className="text-foreground">{fmtInt(calc.current)}</span> · Past-due: <span className="text-foreground">{fmtInt(calc.pastDue)}</span></p>
            <p className="mt-1">Effective monthly servicing: <span className="text-foreground">{fmt(calc.effectiveMonthly)}</span></p>
            <p className="mt-1">IBA implied bps: <span className="text-primary">{calc.ibaBps.toFixed(1)} bps</span> · market 75–150 bps</p>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-7 space-y-6">
          {/* Hero savings */}
          <div className="bg-white border-t-4 border-t-primary border-x border-b border-foreground/20 p-8 md:p-10">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-brand text-muted-foreground flex items-center gap-2">
                  <TrendingDown className="w-3.5 h-3.5" /> Year-1 savings vs traditional servicer
                </p>
                <p
                  data-testid="annual-savings-value"
                  className="font-mono-data text-4xl sm:text-5xl md:text-6xl mt-3 tabular text-foreground leading-[1] break-all"
                >
                  {fmt(calc.y1Savings)}
                </p>
                <p className="mt-3 text-sm text-muted-foreground font-mono-data">
                  ↓ {(calc.savingsPct * 100).toFixed(1)}% lower cost to serve
                </p>
              </div>
              <div className="hidden md:block text-right">
                <p className="overline">3-year total gain</p>
                <p className="font-mono-data text-3xl mt-2">{fmt(calc.threeYearGain)}</p>
                <p className="text-[11px] text-muted-foreground mt-1 font-mono-data">
                  incl. revenue share
                </p>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-foreground/15 grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-[11px] uppercase tracking-brand text-muted-foreground">
                  + Revenue share returned to you (Year 1)
                </p>
                <p
                  data-testid="revenue-share-value"
                  className="font-mono-data text-3xl md:text-4xl mt-2 text-foreground tabular"
                >
                  + {fmt(calc.revenueShareBack)}
                </p>
                <p className="mt-2 text-xs text-muted-foreground font-mono-data">
                  50 / 50 split on {fmt(calc.ancillaryAnnual)} in ancillary revenue
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-brand text-muted-foreground">
                  Total Year-1 economic gain
                </p>
                <p
                  data-testid="total-gain-value"
                  className="font-mono-data text-3xl md:text-4xl mt-2 tabular text-primary"
                >
                  {fmt(calc.y1EconomicGain)}
                </p>
                <p className="mt-2 text-xs text-muted-foreground font-mono-data">
                  savings + revenue share combined
                </p>
              </div>
            </div>
          </div>

          {/* Side-by-side annual */}
          <div className="grid md:grid-cols-2 gap-px bg-foreground/20 border border-foreground/20">
            <div className="bg-white p-6">
              <p className="overline mb-3">Traditional servicer · Year 1</p>
              <p className="font-mono-data text-3xl">{fmt(calc.competitorAnnual)}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                {fmtInt(activeLoans)} × {fmt(avgUpb)} × {competitorPct.toFixed(2)}%
              </p>
            </div>
            <div className="bg-white p-6 border-l border-foreground/20">
              <p className="overline mb-3">IBA Servicing · Year 1</p>
              <p className="font-mono-data text-3xl text-foreground">{fmt(calc.instoreY1)}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                Includes {fmt(PRICING.onboarding_one_time)} one-time onboarding
              </p>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-white border border-foreground/20 p-6">
            <p className="overline mb-4">3-year cumulative cost</p>
            <div className="h-60 min-w-[1px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <LineChart data={calc.chartData} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid stroke="#EBEAE4" strokeDasharray="0" vertical={false} />
                  <XAxis dataKey="year" stroke="#5C5C58" tick={{ fontFamily: "JetBrains Mono", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis stroke="#5C5C58" tick={{ fontFamily: "JetBrains Mono", fontSize: 11 }} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} axisLine={false} tickLine={false} width={60} />
                  <Tooltip
                    contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A", background: "#fff", fontFamily: "JetBrains Mono", fontSize: 12 }}
                    formatter={(v) => fmt(v)}
                  />
                  <Line type="monotone" dataKey="Traditional" stroke="#0A0A0A" strokeWidth={2} dot={{ r: 4, fill: "#0A0A0A" }} />
                  <Line type="monotone" dataKey="IBA Servicing" stroke="#8C1D18" strokeWidth={2.5} dot={{ r: 4, fill: "#8C1D18" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </SlideFrame>
  );
}

function NumberField({ id, label, value, onChange, min, max, step = 1, prefix, suffix, isFloat }) {
  return (
    <div>
      <label htmlFor={id} className="overline block mb-2">{label}</label>
      <div className="flex items-baseline gap-2">
        {prefix && <span className="font-mono-data text-muted-foreground">{prefix}</span>}
        <input
          id={id}
          data-testid={`input-${id}`}
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            const v = isFloat ? parseFloat(e.target.value) : parseInt(e.target.value, 10);
            onChange(isNaN(v) ? 0 : v);
          }}
          className="input-sharp flex-1"
        />
        {suffix && <span className="font-mono-data text-xs text-muted-foreground whitespace-nowrap">{suffix}</span>}
      </div>
    </div>
  );
}
